import React from "react";
import { useLocation } from "react-router-dom";

import { Breadcrumbs as PyBreadcrumbs, Text } from "@performyard/py-components";

import Breadcrumbs from "../../../../components/Breadcrumbs";

import { useIsNewNavigation } from "../../../../api/hooks/defs/navigation";

import { isFreshTabLoad } from "../../../../utils/breadcrumbOrigin";
import {
	MEETING_BREADCRUMB_SOURCE,
	appendMeetingBreadcrumbSource,
	parseMeetingBreadcrumbSource,
} from "../../utils/breadcrumbSource";

import { config } from "../config";

type MeetingDetailsBreadcrumbsProps = {
	workspaceName: string;
	workspaceId: string;
	meetingName: string;
	isAdmin?: boolean;
};

const MeetingDetailsBreadcrumbs = ({
	workspaceName,
	workspaceId,
	meetingName,
	isAdmin = false,
}: MeetingDetailsBreadcrumbsProps): React.ReactElement => {
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	const source = parseMeetingBreadcrumbSource(location.search);
	const previousSearchParams = new URLSearchParams(location.search).get("from") || "";

	const workspaceUrl = previousSearchParams
		? `/meeting-workspaces/${workspaceId}${previousSearchParams}`
		: `/meeting-workspaces/${workspaceId}`;

	if (isNewNav) {
		if (source === MEETING_BREADCRUMB_SOURCE.dashboard || isFreshTabLoad()) {
			return (
				<PyBreadcrumbs
					items={[
						{ pageId: "meetingWorkspaces", name: "Dashboard", href: "/" },
						{ pageId: "meetingDetails", name: meetingName, href: location.pathname },
					]}
				/>
			);
		}

		const rootItem =
			source === MEETING_BREADCRUMB_SOURCE.companyMeetings
				? {
						pageId: "meetingWorkspaces",
						name: "Company Meetings",
						href: "/company/meetings?name=meetingDate&direction=desc",
					}
				: source === MEETING_BREADCRUMB_SOURCE.actionItems
					? {
							pageId: "meetingWorkspaces",
							name: "My Action Items",
							href: "/meeting-workspaces/?tab=action-items",
						}
					: { pageId: "meetingWorkspaces", name: "My Meeting Workspaces", href: "/meeting-workspaces" };

		return (
			<PyBreadcrumbs
				items={[
					rootItem,
					{
						pageId: "workspaceDetails",
						name: workspaceName,
						href: appendMeetingBreadcrumbSource(workspaceUrl, source),
					},
					{ pageId: "meetingDetails", name: meetingName, href: location.pathname },
				]}
			/>
		);
	}

	const pages = [
		{
			pageID: "meetingWorkspaces",
			label: isAdmin ? "Meetings" : "Meeting Workspaces",
			url: isAdmin ? "/company/meetings" : "/meeting-workspaces",
		},
		{
			pageID: "workspaceDetails",
			label: (
				<div className="py-text-truncate" style={{ maxWidth: config.layout.breadcrumbMaxWidth }}>
					<Text size="paragraph-2" weight="regular" overrideElement="span" wrap={false}>
						{workspaceName}
					</Text>
				</div>
			),
			url: workspaceUrl,
		},
		{
			pageID: "meetingDetails",
			label: (
				<div className="py-text-truncate" style={{ maxWidth: config.layout.breadcrumbMaxWidth }}>
					<Text size="paragraph-2" weight="regular" overrideElement="span" wrap={false}>
						{meetingName}
					</Text>
				</div>
			),
			url: "",
		},
	];

	return <Breadcrumbs pages={pages} />;
};

export default MeetingDetailsBreadcrumbs;
